var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import classNames from 'classnames';
import { useUXPContext } from '@core/context/useUXPContext';
import { SlideInPanel } from '@widget/other/SlideInPanel';
import { FontAwesomeIconWrapper } from '@icon/fontawesome/FontAwesomeIconWrapper';
import Button from '@widget/buttons/Button';
import IconButton from '@widget/buttons/IconButton';
import GalleryView from './GalleryView';
import UrlView from './UrlView';
import UploadView from './UploadView';
import LibraryView, { LIBRARY_ALL_TYPES } from './LibraryView';
import IconsView from './IconsView';
import MediaStripCard from './MediaStripCard';
import MediaStage from './MediaStage';
import Chip from '@widget/other/Chip';
import { Loading } from '@widget/loaders';
import MobileToolbar from '@widget/other/MobileToolbar';
import useIsMobileLayout from '@hooks/useIsMobileLayout';
import useDebounce from '@hooks/useDebounce';
import { normalizeUrl } from '../FileInput/fileHelper';
import { addUnique, formatFileSize, isCroppableImage, libraryTypes, mediaItemUrl, mediaValueName, mediaValueType, resolveAllowedTypes, runFileCheck, runMediaCheck, useMediaLibraryAvailable, useLibraryUpload } from './mediaLibrary';
import MediaCheckNotice from './MediaCheckNotice';
import { canReadImageLink } from './importImage';
import CameraCapture from '@widget/other/CameraCapture/CameraCapture';
import ImageCropper from '@widget/other/ImageCropper/ImageCropper';
import NotificationBlock from '@helper/NotificationBlock';
import ActionsSheet from '@widget/popups/ActionsSheet';
import MobileSheet from '@widget/popups/MobileSheet';
import Input from '../Input';
export { resolveAllowedTypes };
// Sidebar order — fixed, so a picker's sources don't shuffle with the props order.
const SOURCE_ORDER = ['library', 'gallery', 'icons', 'upload', 'camera', 'url'];
const SOURCE_ICONS = {
    library: ['fal', 'folder-open'],
    gallery: ['fal', 'images'],
    icons: ['fal', 'icons'],
    upload: ['fal', 'cloud-upload'],
    url: ['fal', 'link'],
    camera: ['fal', 'camera'],
};
// The sources that browse an existing collection — a list column beside the
// preview column. Upload and URL produce new values instead.
const LIST_SOURCES = ['library', 'gallery', 'icons'];
/**
 * @export
 * The media browse dialog: a source rail beside a list-and-preview pane over
 * the user's library, the platform image gallery, font icons, an upload
 * drop-zone and a plain URL entry — whichever of those the field enables.
 *
 * @partOf MediaPicker
 */
const MediaPickerModal = (props) => {
    var _a;
    const { show, onClose, onSelect, multiple, initialSelection, onSelectMultiple, mediaTypes, sources, defaultSource = 'library', allowedTypes, saveToLibrary, uploadPath, searchParameters, currentValue, onUpload, onPick, validate, onRefreshRules, cropRules, title, rules, initialFiles, } = props;
    const context = useUXPContext();
    // Camera photos upload like the Upload source (or through the host's onUpload)
    const cameraUploader = React.useMemo(() => onUpload ? (f) => onUpload(f, 'camera') : undefined, [onUpload]);
    const fileUploader = React.useMemo(() => onUpload ? (f) => onUpload(f, 'upload') : undefined, [onUpload]);
    const { uploadToLibrary: uploadCameraPhoto } = useLibraryUpload({ uploadPath, saveToLibrary, uploader: cameraUploader });
    // A cropped preview uploads like a chosen file
    const { uploadToLibrary: uploadCroppedFile } = useLibraryUpload({ uploadPath, saveToLibrary, uploader: fileUploader });
    // A pick waiting on the host's onPick, and why it failed
    const [picking, setPicking] = React.useState(false);
    const [pickError, setPickError] = React.useState('');
    // The gallery's photo details by URL, for onPick
    const pickMeta = React.useRef({});
    const [source, setSource] = React.useState(defaultSource);
    const [urlEntry, setUrlEntry] = React.useState('');
    const [selection, setSelection] = React.useState([]);
    // Every list previews before it commits: the preview column shows a value
    // and the button that commits it is built here, by the modal.
    const [previewValue, setPreviewValue] = React.useState('');
    // The upload view picks before it uploads, the same way: it reports the
    // files it is holding, and the button that uploads them lives here too.
    const [pendingFiles, setPendingFiles] = React.useState([]);
    // A chosen file failed the field's check (or is still being checked)
    const [uploadBlocked, setUploadBlocked] = React.useState(false);
    const [uploadRequestedAt, setUploadRequestedAt] = React.useState(0);
    // The library's search and type filter live here, so that on phones the
    // bottom toolbar can drive them. The rows it has loaded are kept to show
    // the previewed file's details.
    const [libraryQuery, setLibraryQuery] = React.useState('');
    const [libraryType, setLibraryType] = React.useState(LIBRARY_ALL_TYPES);
    const [typeSheetOpen, setTypeSheetOpen] = React.useState(false);
    // Phones: the library's folders sheet opens from the toolbar; the gallery's search sits there
    const [treeOpen, setTreeOpen] = React.useState(false);
    const [libraryFiltered, setLibraryFiltered] = React.useState(false);
    const [libraryHasTree, setLibraryHasTree] = React.useState(false);
    const [libraryFolderLabel, setLibraryFolderLabel] = React.useState('');
    const [moreOpen, setMoreOpen] = React.useState(false);
    const [galleryQuery, setGalleryQuery] = React.useState('');
    // Single upload: the refused file's crop, for the phone toolbar
    const [uploadFix, setUploadFix] = React.useState(null);
    const [libraryItems, setLibraryItems] = React.useState([]);
    // The field's check: why the previewed (or typed) value can't be used, and
    // why the last camera photo was refused. Bumped by the refresh button to
    // run every check again.
    const [previewProblem, setPreviewProblem] = React.useState('');
    const [cameraProblem, setCameraProblem] = React.useState('');
    // A check is running: the commit waits for its answer
    const [previewChecking, setPreviewChecking] = React.useState(false);
    const [checkKey, setCheckKey] = React.useState(0);
    const [refreshing, setRefreshing] = React.useState(false);
    // "Crop and resize": the camera photo that failed the check, the picture being
    // cropped (and where it came from), and the fetch of a previewed picture
    const [cameraFile, setCameraFile] = React.useState(null);
    const [cropTarget, setCropTarget] = React.useState(null);
    const [cropLoading, setCropLoading] = React.useState(false);
    // What the caller asked for, before the server gets a say.
    const requestedSources = React.useMemo(() => {
        const defaults = {
            library: true,
            upload: true,
            url: true,
            gallery: (mediaTypes || []).indexOf('Image') >= 0,
            icons: (mediaTypes || []).indexOf('Icon') >= 0,
            // Off unless the field asks for it
            camera: false,
        };
        return SOURCE_ORDER.filter(s => (sources === null || sources === void 0 ? void 0 : sources[s]) !== undefined ? !!sources[s] : defaults[s]);
    }, [mediaTypes, sources]);
    // Only ask the server when the answer could change what renders.
    const wantsLibrary = requestedSources.indexOf('library') >= 0;
    const libraryAvailable = useMediaLibraryAvailable(show && wantsLibrary);
    // A deployment without the media library loses the source outright — this
    // deliberately overrides an explicit `sources={{ library: true }}`, because
    // capability isn't a preference.
    const enabledSources = React.useMemo(() => libraryAvailable === false ? requestedSources.filter(s => s !== 'library') : requestedSources, [requestedSources, libraryAvailable]);
    // Nothing renders until we know, so a source that is about to disappear
    // never gets shown — and `library` is the default, so without this an older
    // deployment would open straight onto a tab that vanishes underneath it.
    // Only ever waits on the first open; the probe is cached from then on.
    const ready = !wantsLibrary || libraryAvailable !== null;
    // Files handed in at open (a drop on the field), given once to the upload view
    const [openFiles, setOpenFiles] = React.useState(undefined);
    // Every open starts from the same source with the caller's current selection.
    React.useEffect(() => {
        if (!show || !ready)
            return;
        const withFiles = !!(initialFiles === null || initialFiles === void 0 ? void 0 : initialFiles.length) && enabledSources.indexOf('upload') >= 0;
        setOpenFiles(withFiles ? initialFiles : undefined);
        setSource(withFiles ? 'upload' : enabledSources.indexOf(defaultSource) >= 0 ? defaultSource : enabledSources[0]);
        setUrlEntry('');
        setSelection(initialSelection || []);
        setPreviewValue('');
        setPendingFiles([]);
        setUploadRequestedAt(0);
        setPickError('');
        setLibraryQuery('');
        setLibraryType(LIBRARY_ALL_TYPES);
        setTypeSheetOpen(false);
        setPreviewProblem('');
        setCameraProblem('');
        setCameraFile(null);
        setCropTarget(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, ready]);
    // The reset above only runs on open, so a source list that changes while the
    // dialog is up would otherwise leave `source` pointing at a rail item that no
    // longer exists — and renderSource() would keep rendering it.
    React.useEffect(() => {
        if (!enabledSources.length || enabledSources.indexOf(source) >= 0)
            return;
        setSource(enabledSources[0]);
    }, [enabledSources, source]);
    // A preview belongs to the source that produced it — carrying a library
    // image into the gallery's preview column would be nonsense.
    React.useEffect(() => {
        setPreviewValue('');
        setCameraProblem('');
        setCameraFile(null);
        // Dropped files belong to the first visit to Upload only
        if (source !== 'upload')
            setOpenFiles(undefined);
    }, [source]);
    const effectiveAllowedTypes = allowedTypes !== null && allowedTypes !== void 0 ? allowedTypes : resolveAllowedTypes(mediaTypes);
    // Phones: the sources move from the side rail to a bottom slider, like tabs
    const isMobile = useIsMobileLayout();
    const uploadEnabled = enabledSources.indexOf('upload') >= 0;
    const storedTypes = libraryTypes(mediaTypes);
    // The library row behind the previewed value, for its details
    const previewItem = source === 'library' && previewValue
        ? libraryItems.find(item => mediaItemUrl(context, item.Path) === previewValue)
        : undefined;
    const debouncedUrl = useDebounce(urlEntry, 400);
    // Typed but not yet checked counts as checking
    const checkPending = !!validate && (previewChecking || (source === 'url' && urlEntry !== debouncedUrl));
    // A typed link whose site won't let the browser read the picture: it shows, but can't be
    // cropped, and can't be taken when the host downloads picks (onPick)
    const [linkBlocked, setLinkBlocked] = React.useState(false);
    const linkValue = source === 'url' ? normalizeUrl(debouncedUrl) : '';
    React.useEffect(() => {
        setLinkBlocked(false);
        if (!linkValue)
            return;
        let live = true;
        canReadImageLink(linkValue).then(ok => { if (live)
            setLinkBlocked(!ok); });
        return () => { live = false; };
    }, [linkValue]);
    const showLinkBlocked = linkBlocked && source === 'url';
    const linkBlocksCommit = showLinkBlocked && !!onPick;
    const commitBlocked = !!previewProblem || checkPending || linkBlocksCommit;
    // The value the check looks at: the typed link on the URL source, else the preview
    const checkValue = source === 'url' ? normalizeUrl(debouncedUrl) : previewValue;
    React.useEffect(() => {
        var _a;
        setPreviewProblem('');
        if (!validate || !checkValue) {
            setPreviewChecking(false);
            return;
        }
        setPreviewChecking(true);
        let live = true;
        const info = {
            value: checkValue,
            name: (previewItem === null || previewItem === void 0 ? void 0 : previewItem.Name) || mediaValueName(checkValue),
            mimeType: (previewItem === null || previewItem === void 0 ? void 0 : previewItem.MimeType) || undefined,
            size: (_a = previewItem === null || previewItem === void 0 ? void 0 : previewItem.Size) !== null && _a !== void 0 ? _a : undefined,
        };
        runMediaCheck(validate, info).then(problem => {
            if (!live)
                return;
            setPreviewProblem(problem || '');
            setPreviewChecking(false);
        });
        return () => { live = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkValue, previewItem === null || previewItem === void 0 ? void 0 : previewItem.Key, checkKey, validate]);
    function refreshRules() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!onRefreshRules)
                return;
            setRefreshing(true);
            try {
                yield onRefreshRules();
            }
            finally {
                setRefreshing(false);
                setCheckKey(k => k + 1);
                setCameraProblem('');
            }
        });
    }
    function isSelected(value) {
        return !!multiple && selection.indexOf(value) >= 0;
    }
    function toggle(value) {
        setSelection(prev => prev.indexOf(value) >= 0 ? prev.filter(v => v !== value) : [...prev, value]);
    }
    /**
     * What a click on an existing item means: single mode previews it (the
     * header button commits), multiple mode toggles it straight away.
     */
    function pick(value) {
        if (!multiple) {
            setPreviewValue(value);
            return;
        }
        toggle(value);
    }
    /** Open a value in the preview column without touching the selection. */
    function preview(value) {
        setPreviewValue(value);
    }
    /** A newly produced value (upload / typed URL) — never a toggle. */
    function add(value) {
        if (!multiple) {
            onSelect === null || onSelect === void 0 ? void 0 : onSelect(value);
            onClose();
            return;
        }
        setSelection(prev => addUnique(prev, [value]));
    }
    /** Several newly produced values at once — a multi-file upload. */
    function addMany(values) {
        if (!(values === null || values === void 0 ? void 0 : values.length))
            return;
        if (!multiple) {
            onSelect === null || onSelect === void 0 ? void 0 : onSelect(values[0]);
            onClose();
            return;
        }
        setSelection(prev => addUnique(prev, values));
    }
    function applyTypedUrl() {
        const url = normalizeUrl(urlEntry);
        if (!url || commitBlocked)
            return;
        if (!multiple)
            return commitPick(url, 'url');
        add(url);
        setUrlEntry('');
    }
    function confirmSelection() {
        onSelectMultiple === null || onSelectMultiple === void 0 ? void 0 : onSelectMultiple(selection);
        onClose();
    }
    /**
     * The confirm for whatever the preview column is showing. Only single mode
     * has one — multiple mode selects by clicking a tile — so it emits and
     * closes, exactly as a freshly produced value does.
     */
    function confirmPreview() {
        if (!previewValue || commitBlocked)
            return;
        commitPick(previewValue, source);
    }
    /** A picked value: through the host's onPick first, when there is one */
    function commitPick(value, from) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!onPick)
                return add(value);
            setPicking(true);
            setPickError('');
            try {
                const result = yield onPick({ value, source: from, meta: pickMeta.current[value] });
                add(typeof result === 'string' && result ? result : value);
            }
            catch (e) {
                setPickError((e === null || e === void 0 ? void 0 : e.message) || context.$L('uxp-core.media-picker.save-failed'));
            }
            finally {
                setPicking(false);
            }
        });
    }
    /** Fetch the previewed (or typed) picture and open it in the cropper */
    function cropPreview(value) {
        return __awaiter(this, void 0, void 0, function* () {
            setCropLoading(true);
            setPickError('');
            try {
                const res = yield fetch(value);
                const blob = res.ok ? yield res.blob() : null;
                if (!blob || !isCroppableImage(blob.type))
                    throw new Error('not an image');
                setCropTarget({ file: new File([blob], mediaValueName(value) || 'image', { type: blob.type }), from: 'preview' });
            }
            catch (_a) {
                setPickError(context.$L('uxp-core.image-cropper.load-failed'));
            }
            finally {
                setCropLoading(false);
            }
        });
    }
    /** The cropped picture: checked again, uploaded like a chosen file (or camera photo), then selected */
    function applyCrop(file) {
        return __awaiter(this, void 0, void 0, function* () {
            const from = cropTarget === null || cropTarget === void 0 ? void 0 : cropTarget.from;
            const problem = yield runFileCheck(validate, file);
            if (problem) {
                setCropTarget(null);
                if (from === 'camera')
                    setCameraProblem(problem);
                else
                    setPickError(problem.split('\n').join(' '));
                return;
            }
            try {
                const url = yield (from === 'camera' ? uploadCameraPhoto : uploadCroppedFile)(file);
                setCropTarget(null);
                setCameraProblem('');
                setCameraFile(null);
                setPreviewValue('');
                if (source === 'url')
                    setUrlEntry('');
                add(url);
            }
            catch (e) {
                setCropTarget(null);
                setPickError((e === null || e === void 0 ? void 0 : e.message) || context.$L('uxp-core.media-picker.upload-failed'));
            }
        });
    }
    /** The "Crop and resize" button for a failing notice, or nothing */
    function cropAction(onClick) {
        if (!cropRules)
            return undefined;
        return _jsx(Button, { title: context.$L('uxp-core.image-cropper.crop-and-resize'), leftIcon: "fal crop-simple", variant: "secondary", size: "small", loading: cropLoading, disabled: cropLoading, onClick: onClick });
    }
    // A previewed or typed value that failed the check, when it is an image
    const previewCropAction = previewProblem && checkValue && mediaValueType(checkValue) === 'Image' && !showLinkBlocked
        ? cropAction(() => cropPreview(checkValue))
        : undefined;
    const commitLabel = context.$L('uxp-core.text.select');
    const cropBelow = context.$L('uxp-core.media-picker.crop-below');
    const sourceLabels = {
        library: context.$L('uxp-core.media-picker.library'),
        gallery: context.$L('uxp-core.text.gallery'),
        icons: context.$L('uxp-core.media-picker.icons'),
        upload: context.$L('uxp-core.text.upload'),
        url: context.$L('uxp-core.text.url'),
        camera: context.$L('uxp-core.media-picker.camera'),
    };
    /**
     * The commit for the previewed value, in the header beside every other
     * commit. Single mode only — multiple mode gathers by clicking tiles and
     * ends the dialog from "Select & close (N)" instead.
     */
    const previewCommit = multiple || !previewValue ? null : _jsx(Button, { className: "uxpcore_mediapicker__topbar-action", title: commitLabel, variant: "primary", size: "small", leftIcon: "phl:check", loading: picking, disabled: picking || commitBlocked, onClick: confirmPreview });
    // The commit for the current source: Select (single) or Select N (multiple)
    const commitButtons = _jsxs(_Fragment, { children: [source === 'upload' && !multiple &&
                _jsx(Button, { className: "uxpcore_mediapicker__topbar-action", title: commitLabel, variant: "primary", size: "small", leftIcon: "phl:check", disabled: pendingFiles.length === 0 || uploadBlocked, onClick: () => setUploadRequestedAt(Date.now()) }), source === 'url' && !multiple &&
                _jsx(Button, { className: "uxpcore_mediapicker__topbar-action", title: commitLabel, variant: "primary", size: "small", leftIcon: "phl:check", disabled: !urlEntry.trim() || picking || commitBlocked, loading: picking, onClick: applyTypedUrl }), previewCommit, multiple &&
                _jsx(Button, { className: "uxpcore_mediapicker__topbar-action", title: context.$L('uxp-core.media-picker.select-count', { count: selection.length }), variant: "primary", size: "small", leftIcon: "phl:check", disabled: selection.length === 0, onClick: confirmSelection })] });
    const topbar = _jsxs("div", { className: "uxpcore_mediapicker__topbar", children: [_jsxs("div", { className: "uxpcore_mediapicker__title", children: [_jsx("span", { children: title || context.$L('uxp-core.media-picker.select-media') }), !!(rules === null || rules === void 0 ? void 0 : rules.length) && !isMobile && _jsx("span", { className: "uxpcore_mediapicker__rules", children: rules.map(rule => _jsx(Chip, { label: rule, size: "small", variant: "secondary" }, rule)) })] }), _jsxs("div", { className: "uxpcore_mediapicker__topbar-actions", children: [!isMobile && commitButtons, onRefreshRules &&
                        _jsx(IconButton, { icon: "fal rotate-right", tooltip: context.$L('uxp-core.media-picker.refresh-rules'), variant: "secondary", size: "small", loading: refreshing, disabled: refreshing, onClick: refreshRules }), _jsx(IconButton, { type: "close", variant: "secondary", size: "small", onClick: onClose })] })] });
    /**
     * The preview for the list sources (library, gallery) and the typed link: the check's notice,
     * then the picture on the stage with its facts in the bar. It closes back to the list: phones
     * with a back button (the preview replaces the list there), the web with the stage's close.
     * The typed link's stage stays while there is a link.
     */
    function renderStage(info, type) {
        // Nothing previewed: the list (as a grid) has the whole pane
        if (!previewValue && source !== 'url')
            return null;
        const toggleButton = !previewMode && multiple && previewValue && (!previewProblem || isSelected(previewValue))
            ? _jsx(Button, { title: context.$L(isSelected(previewValue) ? 'uxp-core.media-picker.remove-item' : 'uxp-core.text.add'), leftIcon: isSelected(previewValue) ? 'fal xmark' : 'fal plus', variant: "secondary", size: "small", onClick: () => toggle(previewValue) })
            : undefined;
        return _jsxs("div", { className: "uxpcore_mediapicker__stagecol", children: [previewProblem && source !== 'url' && _jsx(MediaCheckNotice, { message: phoneFix ? `${previewProblem} ${cropBelow}` : previewProblem, action: previewMode ? undefined : previewCropAction }), _jsx(MediaStage, { value: source === 'url' ? normalizeUrl(urlEntry) : previewValue, type: type, info: info, actions: toggleButton, onClose: !isMobile && source !== 'url' ? () => setPreviewValue('') : undefined })] });
    }
    /** The bar's facts for the previewed library file or gallery photo */
    function stageInfo() {
        if (!previewValue)
            return undefined;
        if (source === 'library') {
            const name = (previewItem === null || previewItem === void 0 ? void 0 : previewItem.Name) || mediaValueName(previewValue);
            return _jsxs(_Fragment, { children: [_jsx("b", { children: name }), (previewItem === null || previewItem === void 0 ? void 0 : previewItem.MediaType) ? ` · ${previewItem.MediaType}` : '', (previewItem === null || previewItem === void 0 ? void 0 : previewItem.Size) ? ` · ${formatFileSize(Number(previewItem.Size))}` : ''] });
        }
        // The photographer's credit only: "Powered by Unsplash" sits under the list
        const meta = pickMeta.current[previewValue];
        if (!(meta === null || meta === void 0 ? void 0 : meta.author))
            return undefined;
        return _jsx("a", { href: meta.pageUrl || 'https://unsplash.com', target: "_blank", rel: "noreferrer", children: context.$L('uxp-core.media-picker.photo-by', { author: meta.author }) });
    }
    function renderSource() {
        switch (source) {
            case 'library':
                return _jsxs(_Fragment, { children: [_jsx(LibraryView, { show: show, mediaTypes: mediaTypes, onPick: multiple ? preview : pick, isSelected: multiple ? isSelected : undefined, onToggle: multiple ? toggle : undefined, activeValue: previewValue, query: libraryQuery, onQueryChange: setLibraryQuery, typeFilter: libraryType, onTypeFilterChange: setLibraryType, hideFilters: isMobile, onItemsChange: setLibraryItems, grid: !previewValue, treeOpen: isMobile ? treeOpen : undefined, onTreeOpenChange: setTreeOpen, onFilteredChange: setLibraryFiltered, onTreeChange: setLibraryHasTree, onSelectionLabelChange: setLibraryFolderLabel }), renderStage(stageInfo(), previewItem === null || previewItem === void 0 ? void 0 : previewItem.MediaType)] });
            case 'gallery':
                return _jsxs(_Fragment, { children: [_jsx(GalleryView, { show: show, searchParameters: searchParameters, onPick: (url, meta) => { if (meta)
                                pickMeta.current[url] = meta; (multiple ? preview : pick)(url); }, isSelected: multiple ? isSelected : undefined, onToggle: multiple ? toggle : undefined, activeValue: previewValue, grid: !previewValue, query: galleryQuery, onQueryChange: setGalleryQuery, hideSearch: isMobile }), renderStage(stageInfo(), 'Image')] });
            // IconsView owns its preview column — only it can turn a card into
            // packages, weights and variants.
            case 'icons':
                return _jsx(IconsView, { show: show, onPick: pick, isSelected: multiple ? isSelected : undefined, onPreview: multiple ? preview : undefined, onPendingChange: setPreviewValue, previewSelected: isSelected(previewValue), onPreviewToggle: multiple ? () => toggle(previewValue) : undefined, currentValue: currentValue });
            case 'upload':
                return _jsx(UploadView, { allowedTypes: effectiveAllowedTypes, uploadPath: uploadPath, saveToLibrary: saveToLibrary, multiple: multiple, onPendingFilesChange: setPendingFiles, uploadRequestedAt: uploadRequestedAt, onUploaded: addMany, onRemoved: (url) => setSelection(prev => prev.filter(v => v !== url)), initialFiles: openFiles, uploader: fileUploader, validate: validate, checkKey: checkKey, onBlockedChange: setUploadBlocked, cropRules: cropRules, onFixChange: (fix) => setUploadFix(() => fix), fixInToolbar: isMobile });
            case 'camera':
                return _jsx(CameraCapture, { inline: true, onCapture: (file) => __awaiter(this, void 0, void 0, function* () {
                        // The field's check first: a refused photo is not uploaded, and can be retaken
                        const problem = yield runFileCheck(validate, file);
                        setCameraProblem(problem || '');
                        setCameraFile(problem ? file : null);
                        if (problem)
                            return;
                        try {
                            add(yield uploadCameraPhoto(file));
                        }
                        catch (e) {
                            setPickError((e === null || e === void 0 ? void 0 : e.message) || context.$L('uxp-core.media-picker.upload-failed'));
                        }
                    }), onChooseFile: uploadEnabled ? () => setSource('upload') : undefined });
            case 'url':
                return _jsxs(_Fragment, { children: [_jsx(UrlView, { urlEntry: urlEntry, onUrlChange: setUrlEntry, onApply: applyTypedUrl, multiple: multiple }), !!urlEntry.trim() && renderStage(undefined)] });
            default:
                return null;
        }
    }
    // Two columns beside the rail only when there really is a second column:
    // only a list source has one, and only once something is being previewed —
    // until then the list owns the whole pane. Upload and URL are single column.
    // Phones show the list or the preview, one at a time. Icons keeps its own preview column.
    const split = LIST_SOURCES.indexOf(source) >= 0 && !!previewValue;
    const sourceSwitcher = enabledSources.length > 1 && isMobile;
    // Phones: the library's search and type filter sit in the bottom toolbar,
    // and only while the library is showing
    // Phones: a picked library file or gallery photo fills the screen with its own toolbar
    // (Back, Crop, Select); the list's tools come back with Back
    const previewMode = isMobile && !!previewValue && (source === 'library' || source === 'gallery');
    // Phones: the open source's "Crop and resize" for a refused file. It replaces the toolbar
    // (no slider, no Select): a crop that passes selects or uploads the file by itself
    const phoneFix = !isMobile ? null
        : previewMode ? (previewCropAction ? () => cropPreview(previewValue) : null)
            : multiple ? null
                : source === 'url' ? (previewCropAction ? () => cropPreview(checkValue) : null)
                    : source === 'camera' ? (cameraFile && cropRules ? () => setCropTarget({ file: cameraFile, from: 'camera' }) : null)
                        : source === 'upload' ? uploadFix : null;
    const libraryTools = isMobile && !previewMode && source === 'library' && ready && enabledSources.indexOf('library') >= 0;
    const galleryTools = isMobile && !previewMode && source === 'gallery' && ready;
    const toolbarActions = previewMode ? [
        ...(multiple && (!previewProblem || isSelected(previewValue)) ? [{
                id: 'toggle',
                icon: isSelected(previewValue) ? 'phr x' : 'phr plus',
                label: context.$L(isSelected(previewValue) ? 'uxp-core.media-picker.remove-item' : 'uxp-core.text.add'),
                onClick: () => toggle(previewValue),
            }] : []),
    ] : [
        // Phones: the library's search, folders and type sit behind one ⋯, so the source name fits
        ...(libraryTools ? [{
                id: 'more',
                icon: 'phr dots-three',
                label: context.$L('uxp-core.text.more'),
                active: moreOpen,
                dot: !!libraryQuery || libraryFiltered || libraryType !== LIBRARY_ALL_TYPES,
                onClick: () => setMoreOpen(true),
            }] : []),
    ];
    const typeOptions = [
        { value: LIBRARY_ALL_TYPES, label: context.$L('uxp-core.media-picker.type-all') },
        ...storedTypes.map(t => ({ value: t, label: context.$L(`uxp-core.media-picker.type-${t.toLowerCase()}`) })),
    ];
    return _jsxs(SlideInPanel, { isOpen: show, onClose: onClose, mode: "full", wrapperClassName: "uxpcore_mediapicker__panel", children: [_jsxs("div", { className: "uxpcore_mediapicker__shell", children: [topbar, !ready
                        ? _jsx("div", { className: "uxpcore_mediapicker__layout uxpcore_mediapicker__layout--loading", children: _jsx(Loading, {}) })
                        : _jsxs("div", { className: "uxpcore_mediapicker__layout", children: [enabledSources.length > 1 && !isMobile &&
                                    _jsx("div", { className: "uxpcore_mediapicker__sidebar", children: enabledSources.map(s => _jsxs("div", { className: classNames('uxpcore_mediapicker__sidebar-item', {
                                                'uxpcore_mediapicker__sidebar-item--selected': source === s,
                                            }), title: sourceLabels[s], onClick: () => setSource(s), children: [_jsx("span", { className: "uxpcore_mediapicker__sidebar-icon", children: _jsx(FontAwesomeIconWrapper, { icon: SOURCE_ICONS[s] }) }), _jsx("span", { className: "uxpcore_mediapicker__sidebar-text", children: sourceLabels[s] })] }, s)) }), _jsx("div", { className: "uxpcore_mediapicker__pane", children: enabledSources.length === 0
                                        ? _jsx("div", { className: "uxpcore_mediapicker__message", children: context.$L('uxp-core.media-picker.no-sources') })
                                        : _jsxs(_Fragment, { children: [pickError &&
                                                    _jsx(NotificationBlock, { class: "uxpcore_mediapicker__notice", variant: "danger", mode: "compact", layout: "bordered", icon: true, message: pickError }), source === 'url' && (showLinkBlocked && previewProblem
                                                    ? _jsx(MediaCheckNotice, { message: `${previewProblem.split('\n')[0]}\n${context.$L('uxp-core.media-picker.link-blocked-fix')}` })
                                                    : showLinkBlocked && onPick
                                                        ? _jsx(MediaCheckNotice, { message: `${context.$L('uxp-core.media-picker.link-blocked-copy')}\n${context.$L('uxp-core.media-picker.link-blocked-hint')}` })
                                                        : previewProblem && _jsx(MediaCheckNotice, { message: phoneFix ? `${previewProblem} ${cropBelow}` : previewProblem, action: phoneFix ? undefined : previewCropAction })), source === 'camera' && cameraProblem && _jsx(MediaCheckNotice, { message: phoneFix ? `${cameraProblem} ${cropBelow}` : cameraProblem, action: phoneFix ? undefined : cameraFile ? cropAction(() => setCropTarget({ file: cameraFile, from: 'camera' })) : undefined }), _jsx("div", { className: split ? 'uxpcore_mediapicker__split' : 'uxpcore_mediapicker__content', children: renderSource() })] }) }), multiple && selection.length > 0 && !(isMobile && source === 'upload') &&
                                    _jsx("div", { className: "uxpcore_mediapicker__selection", children: selection.map(value => _jsx(MediaStripCard, { value: value, onRemove: () => toggle(value), removeTitle: context.$L('uxp-core.media-picker.remove-item') }, value)) }), isMobile &&
                                    _jsx(MobileToolbar, { items: sourceSwitcher && !previewMode && !phoneFix ? enabledSources.map(s => ({ id: s, label: sourceLabels[s], icon: SOURCE_ICONS[s].join(' ') })) : undefined, selected: source, onSelect: (id) => setSource(id), search: galleryTools ? {
                                            value: galleryQuery,
                                            onChange: setGalleryQuery,
                                            placeholder: context.$L('uxp-core.media-picker.search-gallery'),
                                        } : undefined, actions: toolbarActions, 
                                        // A refused file that crop can fix: Back and one wide Crop (a passing crop selects the
                                        // file itself, so Select would only sit there greyed out). Otherwise Back and Select.
                                        leading: previewMode || phoneFix ? _jsxs(_Fragment, { children: [previewMode && _jsx(IconButton, { icon: "phr caret-left", tooltip: context.$L('uxp-core.text.back'), variant: "secondary", size: "medium", onClick: () => setPreviewValue('') }), phoneFix && _jsx(Button, { className: "uxpcore_mediapicker__fixbutton", title: context.$L('uxp-core.image-cropper.crop-and-resize'), leftIcon: "phr crop", variant: "primary", loading: cropLoading, disabled: cropLoading, onClick: phoneFix })] }) : undefined, trailing: phoneFix ? undefined : commitButtons, 
                                        // Back and Crop are buttons, not paging: the full toolbar height
                                        slim: false }), isMobile &&
                                    _jsx(MobileSheet, { isOpen: moreOpen, onClose: () => setMoreOpen(false), title: context.$L('uxp-core.text.more'), children: _jsxs("div", { className: "uxpcore_sheetlist", children: [_jsx("div", { className: "uxpcore_mediapicker__moresearch", children: _jsx(Input, { value: libraryQuery, onChange: (v) => setLibraryQuery(v), placeholder: context.$L('uxp-core.media-picker.search-library'), prefix: _jsx(FontAwesomeIconWrapper, { icon: ['fal', 'search'] }), fullWidth: true }) }), libraryHasTree && _jsxs("button", { className: "uxpcore_sheetlist__row", onClick: () => { setMoreOpen(false); setTreeOpen(true); }, children: [_jsx("span", { className: "uxpcore_sheetlist__icon", children: _jsx(FontAwesomeIconWrapper, { icon: ['fal', 'folder'] }) }), _jsx("span", { className: "uxpcore_sheetlist__label", children: context.$L('uxp-core.media-library.folders') }), _jsx("span", { className: "uxpcore_mediapicker__morevalue", children: libraryFolderLabel })] }), storedTypes.length > 1 && _jsxs("button", { className: "uxpcore_sheetlist__row", onClick: () => { setMoreOpen(false); setTypeSheetOpen(true); }, children: [_jsx("span", { className: "uxpcore_sheetlist__icon", children: _jsx(FontAwesomeIconWrapper, { icon: ['fal', 'filter'] }) }), _jsx("span", { className: "uxpcore_sheetlist__label", children: context.$L('uxp-core.media-picker.filter-type') }), _jsx("span", { className: "uxpcore_mediapicker__morevalue", children: (_a = typeOptions.find(o => o.value === libraryType)) === null || _a === void 0 ? void 0 : _a.label })] })] }) }), isMobile &&
                                    _jsx(ActionsSheet, { isOpen: typeSheetOpen, onClose: () => setTypeSheetOpen(false), title: context.$L('uxp-core.media-picker.filter-type'), children: typeOptions.map(o => _jsx(Button, { title: o.label, leftIcon: libraryType === o.value ? 'phr check' : 'phr circle', active: libraryType === o.value, onClick: () => setLibraryType(o.value) }, o.value)) })] })] }), cropRules &&
                _jsx(ImageCropper, { show: !!cropTarget, file: cropTarget === null || cropTarget === void 0 ? void 0 : cropTarget.file, rules: cropRules, onApply: applyCrop, onCancel: () => setCropTarget(null), onChooseAnother: () => {
                        if ((cropTarget === null || cropTarget === void 0 ? void 0 : cropTarget.from) === 'preview')
                            setPreviewValue('');
                        setCropTarget(null);
                    } })] });
};
export default MediaPickerModal;
